/**
 * SIGNET — motion layer
 *
 * Design rules this file is built around:
 *   - Nothing is ever hidden by CSS alone. The `.signet-js` class that enables
 *     every resting state is added here, so a JS failure leaves a fully
 *     legible page rather than a blank one.
 *   - Motion is opt-out at three levels: prefers-reduced-motion, the theme
 *     settings exposed on window.SignetConfig, and a hardware-concurrency
 *     floor for low-end devices.
 *   - Every observer and listener is registered through track() and released
 *     on pagehide, so soft navigations do not accumulate them.
 *   - Nothing here touches cart, product form, variant or payment behaviour.
 */

if (!window.__signetMotion) {
  window.__signetMotion = true;

  const config = Object.assign(
    {
      reveal: true,
      autoHook: true,
      parallax: true,
      cursor: true,
      loader: true,
      speed: 1,
    },
    window.SignetConfig || {}
  );

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Devices that report very few cores get the static presentation. The floor
  // is deliberately low: 4-core machines are ordinary laptops and plenty of
  // phones report 8, so anything higher than 2 would strip motion from most
  // real visitors. Absent API means "assume capable", not "assume weak".
  const lowPower =
    typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 2;

  const motionAllowed = () => !reduced.matches && !lowPower;

  /* ---------------------------------------------------------------- teardown */

  const teardown = [];
  const track = (fn) => teardown.push(fn);

  const on = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    track(() => target.removeEventListener(type, handler, options));
  };

  const observe = (observer) => {
    track(() => observer.disconnect());
    return observer;
  };

  window.addEventListener(
    'pagehide',
    () => {
      teardown.forEach((fn) => {
        try {
          fn();
        } catch {
          /* a failed teardown must not block the rest */
        }
      });
      teardown.length = 0;
    },
    { once: true }
  );

  /* ------------------------------------------------------------------ state */

  // The resting states in signet.css sit behind .signet-js, and they are what
  // makes an element invisible until it is revealed. Opening that gate when no
  // observer could ever fire would hide content permanently, so the class goes
  // on only once we know the reveal pass will really run.
  const canObserve = 'IntersectionObserver' in window;
  const revealActive = config.reveal && canObserve && motionAllowed();

  if (revealActive) root.classList.add('signet-js');
  root.style.setProperty('--signet-motion-scale', motionAllowed() ? String(config.speed) : '0');

  if (!motionAllowed()) root.classList.add('signet-motion-off');

  // A user flipping the OS setting mid-session gets the static page without a
  // reload; the CSS !important resets take over immediately.
  on(reduced, 'change', () => {
    root.classList.toggle('signet-motion-off', !motionAllowed());
    root.style.setProperty('--signet-motion-scale', motionAllowed() ? String(config.speed) : '0');
  });

  /* ----------------------------------------------------------------- loader */

  const loader = document.querySelector('.signet-loader');
  if (loader) {
    const dismiss = () => {
      loader.classList.add('signet-loader--done');
      window.setTimeout(() => loader.remove(), 600);
    };
    if (!config.loader || !motionAllowed()) {
      loader.remove();
    } else if (document.readyState === 'complete' || document.readyState === 'interactive') {
      dismiss();
    } else {
      on(document, 'DOMContentLoaded', dismiss, { once: true });
      // Hard cap: the loader never delays the catalogue for more than 600ms,
      // whatever the network is doing.
      window.setTimeout(dismiss, 600);
    }
  }

  /* ----------------------------------------------------- line-split headings */

  /**
   * Wraps each rendered line of a heading so it can be clipped and revealed
   * independently. Bails out when the element has element children, because
   * splitting those would destroy links, spans and Liquid-injected markup.
   */
  const splitLines = (el) => {
    if (el.dataset.signetSplit === 'done') return;
    if (Array.from(el.childNodes).some((n) => n.nodeType !== Node.TEXT_NODE)) return;

    const text = el.textContent.trim();
    if (!text) return;

    const original = el.innerHTML;
    const words = text.split(/\s+/);

    el.innerHTML = words.map((w) => `<span data-w>${w}</span>`).join(' ');

    const spans = Array.from(el.querySelectorAll('[data-w]'));
    const lines = [];
    let currentTop = null;

    spans.forEach((span) => {
      const top = span.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        lines.push([]);
        currentTop = top;
      }
      lines[lines.length - 1].push(span.textContent);
    });

    if (!lines.length) {
      el.innerHTML = original;
      return;
    }

    el.innerHTML = lines
      .map(
        (line, i) =>
          `<span class="signet-line"><span class="signet-line__inner" style="--signet-line-delay:${
            i * 70
          }ms">${line.join(' ')}</span></span>`
      )
      .join('');

    el.dataset.signetSplit = 'done';
  };

  /* -------------------------------------------------------------- auto-hook */

  /**
   * Applies the reveal markers to sections that do not carry them by hand.
   *
   * Without this, motion only exists on markup written specifically for SIGNET,
   * which means a stock Horizon section animates not at all. Hooking at runtime
   * keeps the effects working on every section — Horizon's, SIGNET's, and any
   * the merchant adds later in the customizer — with no markup duplication.
   *
   * Hand-placed markers always win: anything already carrying a marker is left
   * exactly as authored.
   */
  const hookable = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (/^(SCRIPT|STYLE|TEMPLATE|NOSCRIPT|LINK|DIALOG)$/.test(el.tagName)) return false;
    if (el.hidden || el.classList.contains('signet-reveal')) return false;
    // Never wrap the accelerated checkout or the add-to-cart form: a buy button
    // must not spend a single frame at opacity 0.
    if (el.querySelector('.shopify-payment-button, form[action*="/cart/add"]')) return false;
    if (el.closest('dialog, [popover], .cart-drawer, .theme-drawer, .search-modal')) return false;
    // A zero-area element can never reach a non-zero intersection ratio, so
    // hooking one would hide it for the rest of the session.
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    const pos = getComputedStyle(el).position;
    return pos !== 'fixed' && pos !== 'sticky';
  };

  const pickTargets = (section) => {
    let node = section;
    // Descend through single-child wrappers so the stagger lands on the real
    // content group rather than on one anonymous container.
    for (let depth = 0; depth < 4; depth += 1) {
      const kids = Array.from(node.children).filter(hookable);
      if (kids.length >= 2) return kids;
      if (kids.length === 1) {
        node = kids[0];
        continue;
      }
      break;
    }
    return hookable(section) ? [section] : [];
  };

  if (config.autoHook && revealActive) {
    document.querySelectorAll('#MainContent .shopify-section').forEach((section) => {
      if (section.querySelector('.signet-reveal, [data-signet-lines]')) return;

      pickTargets(section).forEach((el, i) => {
        el.classList.add('signet-reveal');
        el.style.setProperty('--signet-reveal-delay', `${Math.min(i, 5) * 80}ms`);
      });

      const heading = section.querySelector('h1, h2, .h1, .h2');
      // splitLines bails on headings containing elements, so this is safe to
      // apply broadly — it simply does nothing where it cannot work cleanly.
      if (heading && !heading.hasAttribute('data-signet-lines')) {
        heading.setAttribute('data-signet-lines', '');
      }
    });
  }

  if (motionAllowed()) {
    document.querySelectorAll('[data-signet-lines]').forEach(splitLines);
  }

  /* ----------------------------------------------------------- scroll reveal */

  if (revealActive) {
    const revealObserver = observe(
      new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('signet-reveal--in');
            // One-shot: elements do not re-hide on scroll-up, which would read
            // as flicker rather than as motion.
            obs.unobserve(entry.target);
          });
        },
        // threshold 0 rather than a fraction: a fractional threshold is never
        // reached by an element whose box collapses to zero area, and such an
        // element would then stay at opacity 0 permanently.
        { rootMargin: '0px 0px -12% 0px', threshold: 0 }
      )
    );

    document.querySelectorAll('.signet-reveal, [data-signet-lines]').forEach((el, i) => {
      // Stagger only within the first few items of a group; beyond that the
      // delay starts to feel like lag rather than rhythm.
      if (!el.style.getPropertyValue('--signet-reveal-delay')) {
        el.style.setProperty('--signet-reveal-delay', `${Math.min(i, 4) * 60}ms`);
      }
      revealObserver.observe(el);
    });

    /**
     * Last line of defence. Any element the visitor has already scrolled past
     * must be visible, whatever the observer did or failed to do. Deliberately
     * scoped to what is at or above the fold, so content further down keeps its
     * reveal instead of being flushed visible ahead of time.
     */
    let sweeping = false;
    const sweep = () => {
      sweeping = false;
      document.querySelectorAll('.signet-reveal:not(.signet-reveal--in)').forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('signet-reveal--in');
          revealObserver.unobserve(el);
        }
      });
    };
    const requestSweep = () => {
      if (sweeping) return;
      sweeping = true;
      window.requestAnimationFrame(sweep);
    };

    on(window, 'scroll', requestSweep, { passive: true });
    on(window, 'resize', requestSweep, { passive: true });
    window.setTimeout(sweep, 2000);
  }

  /* ------------------------------------------------------ manifesto lighting */

  const manifesto = document.querySelector('[data-signet-manifesto]');
  if (manifesto && motionAllowed() && canObserve) {
    if (manifesto.dataset.signetSplit !== 'done') {
      const words = manifesto.textContent.trim().split(/\s+/);
      manifesto.innerHTML = words.map((w) => `<span class="signet-word">${w}</span>`).join(' ');
      manifesto.dataset.signetSplit = 'done';
    }

    const wordEls = Array.from(manifesto.querySelectorAll('.signet-word'));
    const wordObserver = observe(
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('signet-word--lit');
          });
        },
        { rootMargin: '-25% 0px -35% 0px' }
      )
    );
    wordEls.forEach((w) => wordObserver.observe(w));
  }

  /* --------------------------------------------------------------- parallax */

  if (config.parallax && motionAllowed() && finePointer.matches) {
    const layers = Array.from(document.querySelectorAll('[data-signet-parallax]'));

    if (layers.length) {
      layers.forEach((l) => l.classList.add('signet-parallax'));

      let ticking = false;
      const viewport = () => window.innerHeight || 1;

      const apply = () => {
        ticking = false;
        const h = viewport();
        layers.forEach((layer) => {
          const rect = layer.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > h) return;
          // -1..1 across the viewport.
          const progress = (rect.top + rect.height / 2 - h / 2) / h;
          const depth = parseFloat(layer.dataset.signetParallax) || 0.04;
          // Capped at 3% of the element's own height, per the brief.
          const max = rect.height * 0.03;
          const offset = Math.max(-max, Math.min(max, progress * rect.height * depth));
          layer.style.setProperty('--signet-parallax-y', `${offset.toFixed(2)}px`);
        });
      };

      const request = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(apply);
      };

      on(window, 'scroll', request, { passive: true });
      on(window, 'resize', request, { passive: true });
      apply();
    }
  }

  /* ----------------------------------------------------------------- cursor */

  if (config.cursor && finePointer.matches && !reduced.matches) {
    const dot = document.createElement('div');
    dot.className = 'signet-cursor';
    dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);
    track(() => dot.remove());

    let targetX = 0;
    let targetY = 0;
    let x = 0;
    let y = 0;
    let raf = 0;
    let running = false;

    const loop = () => {
      // Critically damped follow — fluid without lagging far enough behind to
      // feel disconnected from the pointer.
      x += (targetX - x) * 0.18;
      y += (targetY - y) * 0.18;
      dot.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      if (Math.abs(targetX - x) > 0.1 || Math.abs(targetY - y) > 0.1) {
        raf = window.requestAnimationFrame(loop);
      } else {
        running = false;
      }
    };

    on(
      window,
      'pointermove',
      (e) => {
        if (e.pointerType !== 'mouse') return;
        targetX = e.clientX;
        targetY = e.clientY;
        dot.classList.add('signet-cursor--active');
        if (!running) {
          running = true;
          raf = window.requestAnimationFrame(loop);
        }
      },
      { passive: true }
    );

    const interactive = 'a, button, summary, [role="button"], input[type="submit"]';
    on(document, 'pointerover', (e) => {
      if (e.target instanceof Element && e.target.closest(interactive)) {
        dot.classList.add('signet-cursor--over');
      }
    });
    on(document, 'pointerout', (e) => {
      if (e.target instanceof Element && e.target.closest(interactive)) {
        dot.classList.remove('signet-cursor--over');
      }
    });

    on(document, 'pointerleave', () => dot.classList.remove('signet-cursor--active'));
    track(() => window.cancelAnimationFrame(raf));
  }
}
